import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, CheckCircle, Clock, UserPlus } from 'lucide-react';
import { StatsCard, QuickActionCard } from '@/components/dashboard';
import { useDashboardStats } from '../hooks/useDashboardStats';

interface DashboardPageProps {
  userName?: string | undefined;
  isSuperAdmin?: boolean | undefined;
}

export function DashboardPage({
  userName = 'User',
  isSuperAdmin = false,
}: DashboardPageProps): JSX.Element {
  const navigate = useNavigate();
  const { stats, isLoading } = useDashboardStats();

  const getActivePercentage = (): string => {
    if (!stats || stats.totalCompanies === 0) return '0%';
    const percentage = Math.round((stats.activeCompanies / stats.totalCompanies) * 100);
    return `${percentage}% of total`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Welcome back, {userName}!</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Companies"
          value={isLoading ? '...' : (stats?.totalCompanies ?? 0)}
          description={isLoading ? 'Loading...' : `${stats?.suspendedCompanies ?? 0} suspended`}
          icon={Building2}
        />
        <StatsCard
          title="Active Companies"
          value={isLoading ? '...' : (stats?.activeCompanies ?? 0)}
          description={isLoading ? 'Loading...' : getActivePercentage()}
          icon={CheckCircle}
        />
        <StatsCard title="Pending Verifications" value={0} description="Coming soon" icon={Clock} />
        <StatsCard
          title="Total Users"
          value={isLoading ? '...' : (stats?.totalUsers ?? 0)}
          description={isLoading ? 'Loading...' : `${stats?.activeUsers ?? 0} active`}
          icon={Users}
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="mb-4 text-lg font-semibold">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <QuickActionCard
            title="View Companies"
            description="Manage all companies"
            icon={Building2}
            onClick={() => navigate('/companies')}
          />
          {isSuperAdmin && (
            <QuickActionCard
              title="Manage Users"
              description="Manage super administrators"
              icon={UserPlus}
              onClick={() => navigate('/users/super-admins')}
            />
          )}
        </div>
      </div>
    </div>
  );
}
